import { createRouter, createWebHistory, NavigationGuardWithThis, RouteRecordRaw } from 'vue-router'
import { readTelegramInitData } from '@/mini-app/telegramLaunchParams'

import {
  persistReferralCode,
  REGISTRATION_ROUTE_PATH,
} from '@/modules/auth/utils/referralLanding'
import {
  LANDING_VARIANT_QUERY_KEY,
  resolveLandingVariant,
} from '@/modules/auth/utils/landingVariant'
import { captureUtmMarksFromUrl } from '@/modules/auth/utils/utmMarks'
import { captureInvite as persistInviteToken } from '@/modules/vpn/utils/poolInvite'
import { consumeTradeInAuthReturn } from '@/modules/vpn/utils/tradeinClaim'

/**
 * IGRA-45 — общий вход для `/r/:login` и `/c/:login`: сохраняем реферера и
 * маркер приглашения в круг, после чего показываем ЛЕНДИНГ.
 *
 * Раньше отсюда уводило на `/registration`, а `LandingPage` на этом имени
 * роута сама открывает окно входа. Человек, кликнувший ссылку друга, упирался
 * в форму почты раньше, чем успевал прочитать, что ему вообще предлагают:
 * лендинг оставался позади затемнения, и чтобы его увидеть, окно надо было
 * закрыть. Ссылку прислал друг, а встречал её запрос личных данных.
 *
 * Метка реферера при этом никуда не девается — она уже записана строкой выше,
 * до всякого перехода, и переживает и лендинг, и регистрацию.
 *
 * Уходим на `landing`, а не остаёмся на своём адресе: у корневого роута в
 * `beforeEnter` живёт жребий A/B/C, и без него весь трафик по приглашениям
 * шёл бы мимо сплит-теста, всегда видя вариант A.
 *
 * Query переносится в редирект: без этого `?lp=b` на реф-ссылке терялся, и
 * принудительный выбор лендинга (landingVariant.ts) не работал вместе с
 * приглашением — то есть протестировать вариант B на приглашённом госте было
 * нечем.
 */
const captureReferral: NavigationGuardWithThis<undefined> = (to, _from, next) => {
  const login =
    typeof to.params.login === 'string'
      ? to.params.login
      : Array.isArray(to.params.login)
        ? to.params.login[0]
        : ''
  persistReferralCode(login)
  localStorage.setItem('circle_invite', '1')
  next({ name: 'landing', query: to.query })
}

// Referral links to a specific landing do not add the frontend circle marker.
const captureLandingReferral =
  (name: string): NavigationGuardWithThis<undefined> =>
  (to, _from, next) => {
    const raw = to.params.login
    persistReferralCode(typeof raw === 'string' ? raw : Array.isArray(raw) ? (raw[0] ?? '') : '')
    next({ name, query: to.query, hash: to.hash })
  }

/**
 * IGRA-110 — приглашение в общую подписку (`/invite/:token`). В отличие от
 * `captureReferral` человека НИКУДА не уводим: он пришёл читать предложение, и
 * страница обязана открыться. Токен просто кладём рядом с `circle_invite` —
 * вход и регистрация заканчиваются жёстким переходом на `/dashboard`, и без
 * этой записи приглашение не пережило бы вход.
 */
const captureInvite: NavigationGuardWithThis<undefined> = (to, _from, next) => {
  const raw = to.params.token
  persistInviteToken(typeof raw === 'string' ? raw : Array.isArray(raw) ? (raw[0] ?? '') : '')
  next()
}

/**
 * IGRA-110 §11 — флаг `vpn.pool.enabled`. Выключен на сайте — раздела
 * «Поделиться подпиской» нет: адрес `/share` уводит на «Главную», как будто
 * страницы и не было.
 *
 * Флаг знает только бэкенд (`GET /vpn/pool` → `enabled`), поэтому ответ
 * читается до решения — и только если его ещё не читали: из кабинета человек
 * приходит сюда с уже прочитанным пулом («Главная» его запрашивает), и лишнего
 * запроса не будет. Молчащий ответ (сеть отвалилась, ручки ещё нет) НЕ
 * закрывает раздел: спрятать оплаченную возможность из-за сетевой ошибки хуже,
 * чем показать её там, где флаг выключен — приглашения всё равно откажет
 * бэкенд.
 *
 * Приглашение `/invite/:token` этим guard'ом НЕ закрывается и закрыто быть не
 * может: ссылки уже разосланы письмами, и человек на том конце про наш флаг
 * ничего не знает (решение оператора).
 *
 * Здесь же закрыта РОЛЬ. `/share` — кабинет ВЛАДЕЛЬЦА: форма приглашения,
 * «Исключить» напротив каждого соседа, текст «докупи устройства — лимит
 * вырастет у всех». Участнику вкладку прячет `VpnTabNav`, плитку — «Главная»,
 * но прямой заход (кнопка «назад» после принятия приглашения, закладка,
 * история SPA) не перехватывал никто: участник видел владельческий экран, где
 * каждая кнопка кончается отказом бэкенда, а «Исключить» стоит и напротив
 * него самого.
 *
 * Уводим ТОЛЬКО по утвердительному `role === 'member'`. Молчащий или ещё не
 * прочитанный ответ участником никого не делает: редирект по недозагруженному
 * состоянию выкинул бы из раздела и владельца — в том числе того, кто только
 * что вернулся сюда, пока ответ ещё в пути.
 */
const requirePoolEnabled: NavigationGuardWithThis<undefined> = async (_to, _from, next) => {
  const { usePoolStore } = await import('@/modules/vpn/store/poolStore')
  const poolStore = usePoolStore()

  if (!poolStore.hasFetchedOnce) await poolStore.fetchPool()

  if (poolStore.pool?.enabled === false || poolStore.pool?.role === 'member') {
    next({ name: 'dashboard' })
    return
  }

  next()
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'landing',
    // IGRA-48 — A/B: вариант решается один раз и запоминается, победитель
    // определяется по деньгам (RPV и оплата), а не по кликам. Разные URL, а не
    // рендер двух вариантов на одном: так вариант виден в аналитике как путь
    // страницы и его не надо прокидывать отдельным событием.
    beforeEnter: (to) => {
      const variant = resolveLandingVariant(to.query[LANDING_VARIANT_QUERY_KEY])
      if (variant === 'b') return { name: 'landing-b', query: to.query }
      if (variant === 'c') return { name: 'landing-c', query: to.query }

      return true
    },
    component: () => import('@/modules/auth/pages/LandingPage.vue'),
    meta: {
      title: 'nav.titles.landing',
      public: true,
    },
  },
  {
    path: '/b',
    name: 'landing-b',
    // Прямой заход на /b допустим и сам по себе назначает вариант B — иначе
    // жребий мог бы отправить пришедшего по прямой ссылке обратно на A.
    // Уводим отсюда только по явному `?lp=a`.
    beforeEnter: (to) => {
      const forced = to.query[LANDING_VARIANT_QUERY_KEY]
      if (forced === 'a') {
        resolveLandingVariant('a')

        return { name: 'landing', query: to.query }
      }
      if (forced === 'c') {
        resolveLandingVariant('c')

        return { name: 'landing-c', query: to.query }
      }

      resolveLandingVariant('b')

      return true
    },
    component: () => import('@/modules/auth/pages/LandingBPage.vue'),
    meta: {
      title: 'nav.titles.landing',
      public: true,
    },
  },
  {
    // Путь `/c` занят приглашением в круг (`/c/:login`), поэтому лендинг C
    // живёт на `/lp-c`. Жребий его не назначает: сейчас идёт замер A против B,
    // и C открывается только по прямой ссылке или `?lp=c`.
    path: '/lp-c',
    name: 'landing-c',
    beforeEnter: (to) => {
      const forced = to.query[LANDING_VARIANT_QUERY_KEY]
      if (forced === 'a' || forced === 'b') {
        resolveLandingVariant(forced)

        return {
          name: forced === 'a' ? 'landing' : 'landing-b',
          query: to.query,
        }
      }

      resolveLandingVariant('c')

      return true
    },
    component: () => import('@/modules/auth/pages/LandingCPage.vue'),
    meta: {
      title: 'nav.titles.landing',
      public: true,
    },
  },
  {
    path: REGISTRATION_ROUTE_PATH,
    name: 'registration',
    // Тот же выбор варианта, что и на `/`. Без него приглашённый гость всегда
    // видел лендинг A: реф-ссылки редиректят СЮДА, а вариант решался только на
    // корневом роуте. То есть весь трафик по приглашениям шёл мимо жребия и
    // мимо `?lp=`, и сплит-тест сравнивал варианты на неполной выборке.
    beforeEnter: (to) => {
      const variant = resolveLandingVariant(to.query[LANDING_VARIANT_QUERY_KEY])
      if (variant === 'b') return { name: 'landing-b', query: to.query }
      if (variant === 'c') return { name: 'landing-c', query: to.query }

      return true
    },
    component: () => import('@/modules/auth/pages/LandingPage.vue'),
    meta: {
      title: 'nav.titles.landing',
      public: true,
    },
  },
  /**
   * IGRA-434 — лендинг механики Trade-in («перенесите остаток чужого VPN»).
   *
   * Прямой маршрут ВНЕ жребия `landingVariant.ts` (там идёт замер A против B,
   * и третий участник обнулил бы собранные данные), свои компоненты и свой
   * счётчик воронки.
   *
   * `public: true` — страницу открывают по ссылке из рекламы и из бота, в том
   * числе гостем; форма импорта сама скажет, что для переноса нужен вход.
   *
   * Метки `localeFollowsVisitor` здесь НЕТ и быть не должно: страница русская,
   * а метка помечает ровно исключения из правила «сайт по умолчанию русский»
   * (сторож — tests/unit/landingLocaleLock.test.ts).
   */
  {
    path: '/tradein',
    name: 'tradein',
    component: () => import('@/modules/vpn/pages/TradeInPage.vue'),
    meta: {
      title: 'nav.titles.tradein',
      public: true,
    },
  },
  /**
   * FLOW-V4 §2.1 — СЕССИЯ ПЕРЕНОСА: та же страница, но с адресом конкретного
   * переноса. Сюда ведут кнопка «на сайт» в Happ, письма-напоминания и возврат
   * после входа; открывается из любого браузера — права проверяет бэкенд
   * (`GET /state?resume=`). Публичная по той же причине, что и `/tradein`.
   */
  {
    path: '/tradein/s/:resume',
    name: 'tradein-session',
    component: () => import('@/modules/vpn/pages/TradeInPage.vue'),
    meta: {
      title: 'nav.titles.tradein',
      public: true,
    },
  },
  // Sprint 17 ч.2 — страница ключа для i5G. Публичная: гость видит лендинг
  // 5G, вошедший — свой ключ. Сюда ведут реклама 5G, кнопка «Получить ключ»
  // в i5G и `activation_url` из ответа patched-devices.
  {
    path: '/5g',
    name: 'fiveg',
    component: () => import('@/modules/vpn/pages/FiveGPage.vue'),
    meta: { title: 'nav.titles.fiveg', public: true, layout: 'blank' },
  },
  // IGRA-32: public purchase page — reachable by guests (tariff → email →
  // captcha → Cloudpayments redirect; the account is created server-side).
  // HK-IGRA.585 — только для гостя: вошедшего гвард ниже уводит на
  // «Подписку», где та же сетка тарифов без поля почты.
  {
    path: '/buy',
    name: 'buy',
    component: () => import('@/modules/vpn/pages/BuyPage.vue'),
    meta: {
      title: 'nav.titles.buy',
      public: true,
    },
  },
  /**
   * Юридический набор (блокер выката №3): оферта, тарифы, рекуррентные платежи,
   * отмена подписки, возврат, политика ПД.
   *
   * Путь НЕ `/app/law/*` намеренно: `/app` в dev-режиме проксируется на бэкенд
   * (см. vite.config.ts), то есть SPA такой маршрут никогда бы не увидела, а на
   * бою он отдаёт 404 — контентная подсистема сайта для igra.now не наполнена.
   * Открыто гостю: согласиться с офертой человек обязан ДО входа в кабинет.
   */
  {
    path: '/law',
    name: 'legal-index',
    component: () => import('@/modules/legal/pages/LegalPage.vue'),
    meta: {
      title: 'nav.titles.legal',
      public: true,
    },
  },
  {
    path: '/law/:doc',
    name: 'legal-document',
    component: () => import('@/modules/legal/pages/LegalPage.vue'),
    props: (route) => ({ slug: String(route.params.doc ?? '') }),
    meta: {
      title: 'nav.titles.legal',
      public: true,
    },
  },
  /**
   * IGRA-106 — две точки входа для человека, у которого кабинета ещё не было:
   * magic-ссылка из письма об оплате и мост со страницы активации ключа.
   * Оба роута публичные: сюда приходят как раз неавторизованные, а «уже вошёл»
   * страницы разбирают сами — редиректом в кабинет без единого запроса.
   */
  {
    path: '/auth/magic',
    name: 'auth-magic',
    component: () => import('@/modules/auth/pages/MagicLoginPage.vue'),
    meta: {
      title: 'auth.magic.title',
      public: true,
    },
  },
  {
    path: '/auth/key/:uid',
    name: 'auth-key',
    component: () => import('@/modules/auth/pages/KeyLoginPage.vue'),
    props: (route) => ({ uid: String(route.params.uid ?? '') }),
    meta: {
      title: 'auth.keyLogin.title',
      public: true,
    },
  },
  /**
   * IGRA-110 экран 06 — приглашение в общую подписку. Роут ПУБЛИЧНЫЙ: по
   * ссылке из письма приходит человек без аккаунта, а «уже вошёл» страница
   * разбирает сама — предлагает принять приглашение здесь же.
   */
  {
    path: '/invite/:token',
    name: 'pool-invite',
    beforeEnter: captureInvite,
    component: () => import('@/modules/vpn/pages/PoolInvitePage.vue'),
    props: (route) => ({ token: String(route.params.token ?? '') }),
    meta: {
      title: 'nav.titles.poolInvite',
      public: true,
    },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/modules/vpn/pages/VpnDashboardPage.vue'),
    meta: {
      title: 'nav.titles.dashboard',
    },
  },
  /**
   * Экран «Отмена подписки» для ограниченной сессии по ссылке из письма или
   * бота (решение оператора 2026-09-28): только цепочка удержания и отмены,
   * без меню кабинета. Публичный — чтобы протухшая ссылка показала «войдите»,
   * а не выкинула на лендинг; ключа нет — страница сама это покажет.
   */
  {
    path: '/subscription/cancel',
    name: 'subscription-cancel',
    component: () => import('@/modules/vpn/pages/SubscriptionCancelPage.vue'),
    meta: {
      title: 'vpn.cancelScope.title',
      layout: 'blank',
      public: true,
    },
  },
  // HK-IGRA.736 — знакомство после подключения VPN: страны, звёзды, семья,
  // круг, гильдии. Только для вошедших; на весь экран, без оболочки кабинета
  // (экраны как сторис, своя шапка с «Пропустить»).
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/modules/vpn/pages/OnboardingPage.vue'),
    meta: {
      title: 'nav.titles.onboarding',
      layout: 'blank',
    },
  },
  // Постоянный адрес того, что «Главная» показывает один раз до первого
  // подключения: ключ и страница с инструкцией.
  {
    path: '/connect',
    name: 'connect',
    component: () => import('@/modules/vpn/pages/ConnectPage.vue'),
    meta: {
      title: 'nav.titles.connect',
    },
  },
  {
    path: '/routing',
    name: 'routing',
    component: () => import('@/modules/vpn/pages/RoutingPage.vue'),
    meta: {
      title: 'nav.titles.routing',
    },
  },
  {
    path: '/devices',
    name: 'devices',
    component: () => import('@/modules/vpn/pages/DevicesPage.vue'),
    meta: {
      title: 'nav.titles.devices',
    },
  },
  {
    path: '/friends',
    name: 'friends',
    component: () => import('@/modules/vpn/pages/FriendsPage.vue'),
    meta: {
      title: 'nav.titles.friends',
    },
  },
  // IGRA-110 — «Поделиться подпиской»: пул устройств владельца и приглашения.
  {
    path: '/share',
    name: 'pool',
    beforeEnter: requirePoolEnabled,
    component: () => import('@/modules/vpn/pages/PoolPage.vue'),
    meta: {
      title: 'nav.titles.pool',
    },
  },
  // IGRA-104 — «Гильдия»: соревнование веток по сумме игровых баллов.
  {
    path: '/guild',
    name: 'guild',
    component: () => import('@/modules/vpn/pages/GuildPage.vue'),
    meta: {
      title: 'nav.titles.guild',
    },
  },
  // IGRA-111 фаза 2 — «Карта лестницы»: все 15 званий и нормы верхних видны
  // с первого дня (§5.2), чтобы потолок игры в одиночку не был сюрпризом.
  {
    path: '/rank',
    name: 'rank',
    component: () => import('@/modules/vpn/pages/RankLadderPage.vue'),
    meta: {
      title: 'nav.titles.rank',
    },
  },
  // IGRA-112 — «Награды»: витрина бейджей-ачивок, серые до получения.
  {
    path: '/achievements',
    name: 'achievements',
    component: () => import('@/modules/vpn/pages/AchievementsPage.vue'),
    meta: {
      title: 'nav.titles.achievements',
    },
  },
  {
    path: '/gifts',
    name: 'gifts',
    component: () => import('@/modules/vpn/pages/GiftsPage.vue'),
    meta: {
      title: 'nav.titles.gifts',
    },
  },
  {
    path: '/payments',
    name: 'payments',
    component: () => import('@/modules/vpn/pages/PaymentsPage.vue'),
    meta: {
      title: 'nav.titles.payments',
    },
  },
  // IGRA-100 — управление подпиской и единственная точка входа в отписку.
  {
    path: '/subscription',
    name: 'subscription',
    component: () => import('@/modules/vpn/pages/SubscriptionPage.vue'),
    meta: {
      title: 'nav.titles.subscription',
    },
  },
  {
    path: '/affiliate',
    redirect: '/affiliate/partner',
  },
  {
    path: '/affiliate/partner',
    name: 'affiliate-partner',
    component: () => import('@/modules/affiliate/pages/PartnerProgramPage.vue'),
    meta: {
      title: 'nav.titles.affiliatePartner',
    },
  },
  {
    path: '/affiliate/teams',
    name: 'affiliate-teams',
    component: () => import('@/modules/affiliate/pages/AffiliateTeamsPage.vue'),
    meta: {
      title: 'nav.titles.affiliateTeams',
    },
  },
  {
    path: '/affiliate/info',
    redirect: '/affiliate/partner',
  },
  {
    path: '/teams',
    redirect: '/affiliate/teams',
  },
  /**
   * IGRA-TEAM — лендинг для сетевых лидеров MLM.
   *
   * Путь освобождён от редиректа на `/affiliate/teams`: входящих ссылок на
   * него не было (`/app/team` из писем — страница структуры криптокабинета
   * платформы, другой адрес), а `/teams` выше тот же редирект сохраняет.
   *
   * `public: true` и своё имя роута: гвард уводит авторизованных только с
   * `landing` и `registration`, а эту страницу лидер показывает команде, часть
   * которой уже с аккаунтом. В жребий `landingVariant.ts` страница не входит —
   * там идёт замер A против B.
   */
  {
    path: '/team',
    name: 'team-landing',
    component: () => import('@/modules/auth/pages/TeamLandingPage.vue'),
    meta: {
      title: 'nav.titles.teamLanding',
      public: true,
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/modules/profile/pages/ProfilePage.vue'),
    meta: {
      title: 'nav.titles.profile',
    },
  },
  {
    path: '/tma-init',
    name: 'tma-init',
    component: () => import('@/mini-app/TmaInitPage.vue'),
    meta: {
      title: 'Telegram Mini-App',
      public: true,
      tma: true,
    },
  },
  {
    path: '/tradein/r/:login',
    name: 'tradein-referral',
    beforeEnter: captureLandingReferral('tradein'),
    component: () => import('@/modules/vpn/pages/TradeInPage.vue'),
    meta: {
      title: 'nav.titles.tradein',
      public: true,
    },
  },
  {
    path: '/team/r/:login',
    name: 'team-referral',
    beforeEnter: captureLandingReferral('team-landing'),
    component: () => import('@/modules/auth/pages/TeamLandingPage.vue'),
    meta: {
      title: 'nav.titles.teamLanding',
      public: true,
    },
  },
  // IGRA-20 / IGRA-45: /r/:login и /c/:login ведут себя ОДИНАКОВО — любая
  // реферальная ссылка зовёт человека в круг приглашающего (решение оператора
  // 2026-08-15). Оба роута сохраняют реферера (localStorage + `referal` cookie,
  // 30d TTL) и маркер приглашения в круг, после чего отдают человека сразу в
  // регистрацию — маркетингового лендинга между ними нет. Ни один роут не
  // удаляется: разосланные `/c`-ссылки обязаны работать вечно.
  {
    path: '/r/:login',
    name: 'referral-landing',
    beforeEnter: captureReferral,
    component: () => import('@/modules/auth/pages/LandingPage.vue'),
    meta: {
      title: 'nav.titles.landing',
      public: true,
    },
  },
  {
    path: '/c/:login',
    name: 'circle-invite-landing',
    beforeEnter: captureReferral,
    component: () => import('@/modules/auth/pages/LandingPage.vue'),
    meta: {
      title: 'nav.titles.landing',
      public: true,
    },
  },
  {
    path: '/payment/onchainpay',
    name: 'payment-onchainpay',
    component: () => import('@/modules/payment/pages/OnchainpayPage.vue'),
    meta: {
      title: 'onchainpay.title',
      tma: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: () => '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  // HK-IGRA.63: без этого браузер оставлял прежнюю высоту прокрутки — нажав
  // кнопку внизу лендинга, человек попадал на форму покупки где-то посередине
  // и не видел ни выбранного тарифа, ни поля email.
  scrollBehavior(to, from, savedPosition) {
    // «Назад»/«вперёд» — возвращаем туда, где человек был.
    if (savedPosition) return savedPosition
    // Якорь внутри страницы ведёт к своему блоку, а не наверх.
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    // Сменилась только query (фильтр, ?period= на той же странице) — страница
    // та же, перематывать её нельзя.
    if (to.path === from.path) return false

    return { top: 0 }
  },
})

// Clean spa_auth_token from URL immediately on page load (before any navigation)
// This ensures token doesn't remain in address bar even during redirects
let pendingSpaAuthToken: string | null = null

const cleanSpaAuthTokenFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('spa_auth_token')
  if (token) {
    // Save token for later exchange
    pendingSpaAuthToken = token
    // Clean URL immediately
    urlParams.delete('spa_auth_token')
    const cleanSearch = urlParams.toString()
    const cleanUrl =
      window.location.pathname +
      (cleanSearch ? '?' + cleanSearch : '') +
      window.location.hash
    window.history.replaceState({}, '', cleanUrl)
  }
}

// Execute immediately
cleanSpaAuthTokenFromUrl()

// IGRA-393 — utm-метки снимаем с САМОГО первого адреса, тем же приёмом и в том
// же месте, что чистку `spa_auth_token` выше: рекламная ссылка ведёт на обычный
// лендинг или сразу на тариф, своего роута у неё нет, и `beforeEnter` (как у
// `/r/:login`) её бы не поймал. Глобальный `beforeEach` ниже для этого тоже не
// годится: он асинхронный и грузит сторы, а запись меток не имеет права
// зависеть от того, чем закончится их загрузка.
captureUtmMarksFromUrl()

/**
 * Язык сайта зафиксирован (решение оператора): igra.now говорит по-русски и
 * ничего не спрашивает. Исключения помечаются ПОИМЁННО.
 *
 * Почему так, а не список русских страниц: язык SPA брался из cookie
 * `language`, а без неё — из языка браузера (см. `detectLocale`), и гость с
 * английским браузером открывал лендинг с ценами в рублях по-английски.
 * Повлиять на это он не мог — переключателя языка на сайте нет вовсе.
 * Пока правило держится списком, каждая НОВАЯ страница по умолчанию уезжает
 * в английский, и об этом узнаёшь от посетителя. С умолчанием наоборот:
 * забытая страница просто русская, как и весь остальной сайт.
 *
 * `localeFollowsVisitor: true` — «эта страница идёт на языке посетителя».
 * Сейчас метки нет ни у одного маршрута: единственный её носитель, лендинг
 * AdZero, переехал на nobreak.video. Понадобится страница с жёстко
 * английским языком или с переключателем — это отдельное решение оператора,
 * и делается оно здесь же.
 *
 * УЖЕ ВЫБРАННЫЙ язык сильнее умолчания. Есть cookie `language` — работает
 * она, и никакого принуждения не происходит нигде, включая лендинги. Правило
 * «весь сайт по-русски» — это умолчание для того, про кого мы ничего не
 * знаем, а не запрет читать сайт на своём языке.
 *
 * Cookie мы при этом НЕ пишем (`persist = false`). Запись сделала бы
 * умолчание неотличимым от выбора: назначили русский один раз — и человек на
 * странице с меткой больше никогда не увидит свой язык, потому что
 * `detectLocale()` прочитает нашу же запись.
 */
const DEFAULT_LOCALE_CODE = 'ru'

router.beforeEach(async (to) => {
  const { detectLocale, getCurrentLocale, loadLocale, savedLocale } = await import('@/i18n')

  const wanted =
    savedLocale() ??
    (to.meta?.localeFollowsVisitor === true ? detectLocale() : DEFAULT_LOCALE_CODE)
  if (wanted !== getCurrentLocale()) await loadLocale(wanted, false)

  return true
})

/**
 * Точки входа, с которых вошедшего уводим сразу (guard ниже): ВСЕ варианты
 * лендинга и регистрация. Список знал только `landing`, а вход через Google
 * возвращает человека на тот лендинг, где он нажал кнопку, — в прецеденте
 * 2026-09-23 с `/b` он после удачного обмена так и остался гостем на лендинге.
 */
const POST_AUTH_ENTRY_ROUTES = new Set(['landing', 'landing-b', 'landing-c', 'registration'])

// Global navigation guard to protect routes
router.beforeEach(async (to, from, next) => {
  const { useProfileStore } =
    await import('@/modules/profile/store/profileStore')
  const { useLayoutStore } = await import('@/store/layoutStore')
  const { createTokenStorage } = await import('@space/shared-api-client')

  const profileStore = useProfileStore()
  const layoutStore = useLayoutStore()
  const storageKey =
    import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'
  const tokenStorage = createTokenStorage({ storageKey })

  // Handle OAuth token pickup (Google Sign-In redirect)
  // Token was saved from URL before cleaning, now exchange it
  if (pendingSpaAuthToken) {
    const tokenToExchange = pendingSpaAuthToken
    pendingSpaAuthToken = null // Clear to prevent re-exchange
    const tradeInReturn = consumeTradeInAuthReturn()
    let exchanged = false

    const { rememberSessionScope, SUBSCRIPTION_CANCEL_SCOPE } = await import(
      '@/modules/auth/utils/cancelScope'
    )
    // Ссылка отмены из письма/бота даёт ограниченную сессию. Если человек уже
    // вошёл в этом браузере, бэкенд узнаёт его по текущему ключу и вместо
    // ограниченной отдаёт его обычную сессию — поэтому ключ едет с обменом.
    const currentToken = tokenStorage.getToken()
    let cancelScope = false

    // Exchange one-time token for permanent session hash
    try {
      const response = await fetch('/api/auth/exchange-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(currentToken ? { 'x-auth': currentToken } : {}),
        },
        body: JSON.stringify({ token: tokenToExchange }),
      })

      if (response.ok) {
        const data = await response.json()
        // API returns {result: 'success', data: {success: true, token: '...'}}
        const tokenData = data.data ?? data
        if (
          (data.result === 'success' || tokenData.success) &&
          tokenData.token
        ) {
          tokenStorage.setToken(tokenData.token)
          exchanged = true
          console.log('[Router] OAuth token exchanged and stored')
          rememberSessionScope(tokenData.scope ?? null, tokenData.expires_at ?? null)
          cancelScope = tokenData.scope === SUBSCRIPTION_CANCEL_SCOPE

          // Load user profile to complete authentication
          // (ограниченной сессии профиль закрыт — не просим).
          if (!cancelScope) {
            try {
              await profileStore.fetchProfile(true)
              console.log('[Router] Profile loaded after OAuth token exchange')
            } catch (profileError) {
              console.warn(
                '[Router] Failed to load profile after OAuth exchange:',
                profileError,
              )
            }
          }

        } else {
          console.error('[Router] OAuth token exchange failed:', data)
        }
      } else {
        console.error(
          '[Router] OAuth token exchange request failed:',
          response.status,
        )
      }
    } catch (error) {
      console.error('[Router] OAuth token exchange error:', error)
    }

    // Роутер построил первый маршрут раньше, чем адрес вычистили
    // (cleanSpaAuthTokenFromUrl), поэтому `to.query` всё ещё несёт токен, и
    // без этого перехода Vue Router вернул бы его в адресную строку. Так и
    // получалось «/b?spa_auth_token=…» из прецедента 2026-09-23: сгоревший
    // токен жил в адресе, перезагрузка меняла его ещё раз — и снова 406.
    // Уходим на тот же адрес без токена при ЛЮБОМ исходе обмена: при удаче
    // guard ниже увидит ключ сессии и уведёт с лендинга, при неудаче страница
    // откроется гостю, а одноразовый токен второй раз никто не примет.
    // Ограниченная сессия — на отдельный экран отмены, без кабинета.
    if (cancelScope) {
      next({ name: 'subscription-cancel', replace: true })
      return
    }
    const cleanQuery = { ...to.query }
    delete cleanQuery.spa_auth_token
    // Старые данные переноса не управляют главной. Возврат действует только
    // после успешного внешнего входа, начатого с конкретной страницы переноса.
    next({
      path: exchanged && tradeInReturn ? tradeInReturn : to.path,
      query: cleanQuery,
      hash: to.hash,
      replace: true,
    })
    return
  }

  // Ограниченная сессия «только отмена» (решение оператора 2026-09-28):
  // кабинет ей закрыт, любая его страница ведёт на экран отмены. Токен
  // пропал (401) или срок вышел — признак снимается, дальше как гость.
  {
    const { cancelScopeExpired, currentSessionScope, dropCancelScopeSession } =
      await import('@/modules/auth/utils/cancelScope')
    if (currentSessionScope() !== null) {
      if (!tokenStorage.getToken() || cancelScopeExpired()) {
        dropCancelScopeSession()
      } else if (
        to.name !== 'subscription-cancel' &&
        (to.meta?.public !== true || POST_AUTH_ENTRY_ROUTES.has(String(to.name)))
      ) {
        next({ name: 'subscription-cancel', replace: true })
        return
      }
    }
  }

  // Start navigation loading only for actual route changes (different path),
  // not for query-only changes (e.g., filter updates on the same page).
  // Param-only changes within the same route (e.g. /chains/F1 → /chains/F2)
  // reuse the mounted component — overlay would flash the whole page.
  if (from.name && to.path !== from.path && to.name !== from.name) {
    layoutStore.startNavigation()
  }

  const isPublicRoute = to.meta?.public === true
  const hasToken = !!tokenStorage.getToken()

  // If going to landing page and user has auth token, redirect away immediately —
  // the landing is never rendered for an authorized visitor (no landing flash).
  //
  // Сюда же возвращается вход через Google (бэкенд редиректит на `/` с
  // `spa_auth_token`) и через Telegram (`SocialAuthButtons` перезагружает
  // страницу, на которой человек стоял). То есть это ВТОРАЯ точка «только что
  // вошёл» после email+OTP, и решение о цели у обеих одно: тому, у кого
  // подписки никогда не было, кабинет показывать нечего — ведём на витрину.
  // Профиль по-прежнему не ждём; ждём только ответ о подписке, и любой сбой
  // этого ответа возвращает прежнее поведение (см. resolvePostAuthRoute).
  if (POST_AUTH_ENTRY_ROUTES.has(String(to.name)) && hasToken) {
    const { resolvePostAuthRoute } = await import('@/modules/auth/utils/postAuthRoute')
    next(await resolvePostAuthRoute())
    return
  }

  // HK-IGRA.585 (решение оператора 2026-09-18) — витрина `/buy` только для
  // гостя: вошедший человек покупает и продлевает на «Подписке». Сюда же
  // попадают все внутренние переходы на `buy` (пост-вход без подписки,
  // «Настроить VPN» на главной, страница ключа) — им отдельного правила не
  // нужно, редирект один.
  //
  // Исключение — пробник «3 дня за 3 ₽» (`period=trial3`). На «Подписке» его
  // нет и быть не может: кабинетный счёт (`/vpn/billing/checkout`) принимает
  // только month/quarter/year, а пробник продаёт публичный checkout витрины.
  // Без исключения красная кнопка «Главной» и пост-вход без подписки уводили
  // на «Подписку», где пробника не было, — человек не мог его купить. Тому, у
  // кого доступ уже был, пробник не положен (витрина его прячет), и он тоже
  // идёт на «Подписку» — если это уже известно из загруженного overview.
  if (to.name === 'buy' && hasToken) {
    if (to.query.period === 'trial3') {
      const { useVpnStore } = await import('@/modules/vpn/store/vpnStore')
      const overview = useVpnStore().overview
      if (overview === null || overview.access === null) {
        next()
        return
      }
    }
    // FLOW-V3 §2c В-1 — `query` едет С НИМ. Раньше редирект её терял, и
    // «Привязать карту за 3 ₽» на переносе (`/buy?period=trial3&return=/tradein`)
    // приводило вошедшего человека на голую «Подписку»: чем он собирался
    // платить и куда вернуться, по дороге пропадало, а экран переноса ждал
    // его обратно. Адрес переносится целиком, без разбора параметров: что из
    // них читать, решает страница назначения, а не редирект.
    next({ name: 'subscription', query: to.query })
    return
  }

  // TMA init page: if already authenticated, redirect to dashboard
  if (to.name === 'tma-init' && hasToken) {
    // --- Debug breadcrumb ---
    try {
      const { tmaAddBreadcrumb } = await import('@/mini-app/tmaDebugLogger')
      tmaAddBreadcrumb('Router: tma-init + hasToken → redirect /dashboard', {
        from: from.fullPath,
        to: to.fullPath,
      })
    } catch {
      /* ignore */
    }
    next({ name: 'dashboard' })
    return
  }

  // If route is public, allow access
  if (isPublicRoute) {
    next()
    return
  }

  // If user doesn't have token, redirect to landing (or TMA flow if they're a TMA user)
  if (!hasToken) {
    // If user previously entered via Telegram Mini-App, send them back to /tma-init.
    // Только при живой подписи Telegram (initData): без неё /tma-init входа
    // не даст, а встроенный браузер Telegram ту же куку получает и без
    // мини-аппа — гость с оплаченной страницы ключа упирался в тупик
    // «откройте внутри Telegram» (решение оператора 2026-09-23).
    const isTmaUser = document.cookie
      .split('; ')
      .some((c) => c === 'tma_user=1')
    if (isTmaUser && to.name !== 'tma-init' && readTelegramInitData() !== null) {
      // --- Debug breadcrumb ---
      try {
        const { tmaAddBreadcrumb } = await import('@/mini-app/tmaDebugLogger')
        tmaAddBreadcrumb(
          'Router: no token + tma_user cookie → redirect /tma-init',
          {
            from: from.fullPath,
            to: to.fullPath,
          },
        )
      } catch {
        /* ignore */
      }
      next({ name: 'tma-init' })
      return
    }
    next({ name: 'landing' })
    return
  }

  // For all authenticated routes: allow immediate access
  // Profile will be loaded in background by AppLayout's initializeApp()
  // This allows page skeletons to render immediately while data loads
  next()
})

router.afterEach(async (to) => {
  // End navigation loading
  const { useLayoutStore } = await import('@/store/layoutStore')
  const layoutStore = useLayoutStore()

  // Small delay to allow component to mount before hiding loader
  setTimeout(() => {
    layoutStore.endNavigation()
  }, 100)

  if (to.meta?.title) {
    const { i18n } = await import('@/i18n')
    document.title = `${i18n.global.t(to.meta.title as string)} | Igra.now`
  } else {
    document.title = 'Igra.now'
  }
})

// Handle dynamic import errors (e.g., after deployment when chunks have new hashes)
router.onError((error, to) => {
  const isChunkLoadError =
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('Unable to preload CSS')

  if (!isChunkLoadError) return

  // In dev, a chunk failure almost always means Vite's optimized-deps cache is stale
  // (504 "Outdated Optimize Dep"). Silent reload masks the real problem and loops the
  // user back to the previous route — surface the error and instructions instead.
  if (import.meta.env.DEV) {
    console.error(
      '[Router] Dynamic import failed in dev — likely a stale Vite optimize-deps cache.\n' +
        'Fix: stop the dev server, run `rm -rf node_modules/.vite` and restart `npm run dev` (or `npm run dev -- --force`).',
      { to: to.fullPath, error },
    )
    return
  }

  // Prod: чанк не доехал — либо после деплоя сменились хэши, либо моргнула
  // сеть. Сначала пробуем тот же переход ещё раз: это дешевле перезагрузки и
  // спасает от обрыва одного запроса.
  const now = Date.now()
  const retryKey = 'chunk_retry_' + to.fullPath
  const lastRetry = sessionStorage.getItem(retryKey)

  if (!lastRetry || now - parseInt(lastRetry, 10) > 10000) {
    sessionStorage.setItem(retryKey, now.toString())
    console.warn('[Router] Chunk load failed, retrying navigation...', error.message)
    void router.replace(to.fullPath)
    return
  }

  // Повтор не помог — перезагружаем страницу. Именно ПО ЦЕЛЕВОМУ адресу, а не
  // reload(): vue-router меняет адресную строку только после того, как
  // компонент загрузился, поэтому на момент сбоя в ней ещё стоит предыдущий
  // раздел, и reload() возвращал человека туда, откуда он уходил.
  const reloadKey = 'chunk_reload_' + to.fullPath
  const lastReload = sessionStorage.getItem(reloadKey)

  if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
    sessionStorage.setItem(reloadKey, now.toString())
    console.warn('[Router] Chunk load failed after retry, reloading at target path...', error.message)
    window.location.assign(to.fullPath)
  } else {
    console.error('[Router] Chunk load failed even after reload', error)
  }
})

export default router
